// NATIVE
import React, { useEffect, useState } from 'react';

// INSTALLED
import Box from '@mui/material/Box';
import { Formik, Form } from 'formik';
import Step from '@mui/material/Step';
import Stepper from '@mui/material/Stepper';
import StepLabel from '@mui/material/StepLabel';
import { Button, Card, Divider } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

// CUSTOM
import { SnackAlert } from '../../components/SnackAlert';
import { IDatosBasicos } from '../../commons/interfaces';
import MultipleSnackbars from '../../components/MultipleSnackbars';
import { mtDividerUpTable, timeToClosedToast } from '../../commons';
import { closeSessionGlobal, hasPemissions } from '../../commons/utils';
import { SucursalEmpresa } from '../../components/sucursal/SucursalEmpresa';
import { getSucursalDataSelects, saveSucursal } from '../../store/sucursal';
import { BreadCrumbsAndTitle } from '../../components/generics/BreadCrumbsAndTitle';
import { ModalSessionExpired } from '../../components/generics/ModalSessionExpired';
import validationSchema from '../../components/sucursal/formModels/validationSchema';
import formInitialValues from '../../components/sucursal/formModels/formInitialValues';
import createSucursalFormModel from '../../components/sucursal/formModels/createSucursalFormModel';

/**
 * Component for creating a new branch for a company.
 *
 * This component renders a form that allows users to create a new branch for a company.
 * It includes form validation, stepper navigation, and handles form submission with
 * appropriate permission checks and error handling.
 *
 * @component
 * @example
 * return (
 *   <CrearSucursalEmpresa />
 * )
 *
 * @returns {JSX.Element} The CrearSucursalEmpresa component.
 *
 * @remarks
 * - Uses `useDispatch` from `react-redux` to dispatch actions.
 * - Uses `useState` and `useEffect` hooks for managing component state.
 * - Uses `Formik` for form handling and validation.
 * - Displays various alerts and messages based on the form submission status.
 *
 * @function
 * @name CrearSucursalEmpresa
 */
export const CrearSucursalEmpresa = () => {
  // USE DISPATCH
  const dispatch = useDispatch();

  // DATA FORM MODEL
  const { formId, formField } = createSucursalFormModel;

  // USE STATE
  const [alert_snack, setAlertSnack] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [percentaje_c_Company, setPercentajeCCompany] = useState(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [btnText, setBtnText] = React.useState('Crear Sucursal');

  const [modalSessionExpired, setModalSessionExpired] = React.useState(false);
  const [messagesAlerts, setMessagesAlerts] = useState<any>([]);

  const [timeAlert, setTimeAlert] = useState(timeToClosedToast);

  const [msgSnack, setMsgSnack] = React.useState({
    msg: '',
    status: 'success',
  });

  // SCHEMA DATA
  const steps: string[] = ['Crear Sucursal'];
  const currentValidationSchema = validationSchema[activeStep];

  // CONST FUNCTIONS
  const _renderStepContent = (steps: any) => {
    switch (steps) {
      case 0:
        return <SucursalEmpresa formField={formField} />;
    }
  };

  const _submitForm = async (values: any, actions: any) => {
    const havePermissions = await hasPemissions(
      'modulo.configuracion',
      'modulo.sucursal',
      'create'
    );

    if (!havePermissions) {
      await validateLsPermissions('No tienes pemisos para (Crear) sucursal.');

      return false;
    }

    setIsSubmitting(true);
    setBtnText('Un momento...');

    actions.setSubmitting(true);

    const response = await dispatch(saveSucursal(values, setPercentajeCCompany));

    setIsSubmitting(false);
    setBtnText('Crear Sucursal');

    const result = await validateResponseCreate(response, ' (Crear Sucursal.) ');

    if (!result) {
      return false;
    }

    if (response.ok) {
      const resp: any = {
        msg: response.message,
        status: 'success',
      };

      await setMsgSnack(resp);
      await setAlertSnack(true);

      actions.resetForm();
    }
  };

  const validateLsPermissions = async (message: string) => {
    const errors: any = {
      msg: message,
      status: 'error',
    };

    await setMsgSnack(errors);
    await setAlertSnack(true);
  };

  const validateResponseCreate = async (response: any, textActionConcat: string) => {
    if (response.code == 401) {
      await localStorage.removeItem('lastSelectedQypht');
      await closeSessionGlobal();
      setModalSessionExpired(true);
      return false;
    }

    // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION
    if (!response.ok && response.code == 403) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    // 403 EL DATO YA SE ENCUENTRA REGISTRADO
    if (!response.ok && response.code == 409) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO
    if (!response.ok && response.code == 400) {
      const errors: any = [];

      response.errors.forEach((element: any) => {
        errors.push({
          type: 'error',
          text: element,
        });
      });

      setMessagesAlerts(errors);
      return false;
    }

    return true;
  };

  const _handleSubmit = (values: any, actions: any) => {
    _submitForm(values, actions);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <ModalSessionExpired open={modalSessionExpired} />

      <MultipleSnackbars
        messagesList={messagesAlerts}
        position={'bottom-left'}
        autoClose={timeAlert}
      />

      <SnackAlert
        open={alert_snack}
        onclick={setAlertSnack}
        text={msgSnack.msg}
        severity={msgSnack.status}
      />

      <BreadCrumbsAndTitle
        title="Crear Sucursal"
        mainModule="configuracion"
        parent="Listar Sucursales"
        action="/configuracion/sucursales"
      />

      <Divider sx={{ marginBottom: mtDividerUpTable }} />

      <Stepper activeStep={activeStep}>
        {steps.map((label: any, index: any) => {
          const labelProps: {
            optional?: React.ReactNode;
            error?: boolean;
          } = {};

          return (
            <Step key={label}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <Formik
        initialValues={formInitialValues}
        validationSchema={currentValidationSchema}
        onSubmit={_handleSubmit}
      >
        <Card style={{ marginTop: 50, padding: 20 }}>
          <Form id={formId}>
            {_renderStepContent(activeStep)}

            <div style={{ display: 'flex', justifyContent: 'end', marginTop: 25 }}>
              <Button
                disabled={isSubmitting}
                style={{ margin: 5 }}
                type="submit"
                variant="contained"
                color={'success'}
              >
                {btnText}
              </Button>
            </div>
          </Form>
        </Card>
      </Formik>
    </Box>
  );
};
